import { IFormField } from '@ViewModels';

/**
 * Merge the just-saved PUT payload's `customForm.fields` over the form's current copy,
 * preserving local-only metadata the payload does not carry.
 *
 * Why: the V2 sectioned editor edits `surveyForm.surveyParts.*` and only flattens those
 * questions into `customForm.fields` while building the save payload (`createCustomForm`).
 * The form's own `customForm.fields` copy is otherwise written ONCE at mount — and the
 * Preview tab renders question labels/options from that copy. Without writing the saved
 * payload back, Preview keeps showing mount-time data after every save until the survey
 * is closed and re-opened.
 *
 * The payload copy is built client-side (options included), so this does not depend on
 * the PUT response — which strips `options` and, for V2, returns a `customForm` stub with
 * no `fields` array at all (see `patchFieldTypesFromServerResponse` for that constraint).
 *
 * Preserved from the current form copy (matched by field id), because the V2 flatten step
 * drops them:
 * - `_type` — drives the `hasBeenSaved` "Not Saved" indicator
 * - `name`  — response-key isolation for cloned surveys
 *
 * V1 (flat) surveys never need this write-back — their editor edits `customForm.fields`
 * directly, so the payload already equals the form state. The CALLER is responsible for
 * only invoking this on V2 saves (see `useCustomSurveyMutations`).
 *
 * @param savedFields - `customForm.fields` from the mutation payload (`variables.survey`).
 * @param currentFields - the form's current `customForm.fields` (from `getValues()`).
 * @returns the merged array, or `undefined` when there is nothing to write back.
 */
export const mergeSavedPayloadFields = (
	savedFields: IFormField<string>[] | undefined,
	currentFields: IFormField<string>[] | undefined
): IFormField<string>[] | undefined => {
	if (!savedFields?.length) {
		return undefined;
	}
	const currentById = new Map((currentFields ?? []).map(field => [field.id, field]));
	return savedFields.map(saved => {
		const current = currentById.get(saved.id);
		if (!current) {
			return saved;
		}
		return {
			...saved,
			...(current._type ? { _type: current._type } : {}),
			// `!= null` (not truthy) so an empty-string name is preserved as-is.
			...(current.name != null ? { name: current.name } : {}),
		};
	});
};
